import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./master-utilities.module.scss";
import StageTag from "../../../shared/stage-tag/stage-tag";
import StatusCell from "../../../shared/status-cell-render/status-cell";
import Button from "../../ui-kit/button/button";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import {
  fetchProjectFiles,
  fetchProjectLogs,
  getStageKey,
  resetEditAttributesState,
} from "../../../services/reducers/utilities-journal";
import close from "../../../images/master-plan-close.svg";
import { Roles } from "../../../types/roles";
import Checkbox from "../../ui-kit/checkbox/checkbox";
import { stopDrawingTools } from "../../../services/reducers/map";
import VerifiedModal from "./verified-modal/verified-modal";
import { validateConvertedGeometry } from "../../../utils/helpers-for-utilities";
import { ValidationResult } from "../../../interfaces/utilities";
import UploadFilesSection from "./upload-files-section/upload-files-section";
import { useLock } from "./useLock";
import ApproveRejectModal from "./approve-reject-modal/approve-reject-modal";
import SubmissionHistory from "./submission-history/submission-history";
import CustomScrollbar from "../../ui-kit/scrollbar/scrollbar";

const MasterUtilities = () => {
  const { t } = useTranslation();
  const { selectedRow, convertedGeometry, uploadedFiles } = useAppSelector(
    (state) => state.masterUtilities,
  );
  const userRoles = useAppSelector((state) => state.user.user?.roles);
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [
    isVisibleModalVerifiedLoadedObjects,
    setIsVisibleModalVerifiedLoadedObjects,
  ] = useState<boolean>(false);
  const [isVisibleApproveRejectModal, setIsVisibleApproveRejectModal] =
    useState(false);
  const [approvalAction, setApprovalAction] = useState<
    "approve" | "reject" | null
  >(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isPaymentDataSet = !!selectedRow?.workPermitDatePayment;
  const isDepositAlreadyTrue = !!selectedRow?.depositPayed;
  const depositCheckboxDisabled = isDepositAlreadyTrue;

  const [isPayed, setIsPayed] = useState<boolean>(
    isDepositAlreadyTrue || isPaymentDataSet,
  );

  const [depositPayedForSubmit, setDepositPayedForSubmit] = useState<
    boolean | undefined
  >(undefined);

  const isContractor = userRoles?.includes(Roles.Contractor);

  const lock = useLock();

  useEffect(() => {
    return () => {
      dispatch(resetEditAttributesState());
    };
  }, []);

  useEffect(() => {
    if (!selectedRow) {
      navigate("/repository/utilities");
    }
  }, [selectedRow, userRoles]);

  useEffect(() => {
    const projectId = selectedRow?.projectId;
    if (!projectId) return;
    dispatch(fetchProjectFiles(projectId));
    dispatch(fetchProjectLogs(projectId));
  }, [selectedRow?.projectId]);

  useEffect(() => {
    if (depositCheckboxDisabled) return;
    setIsPayed(isDepositAlreadyTrue || isPaymentDataSet);

    return () => {
      dispatch(stopDrawingTools());
    };
  }, [isPaymentDataSet, isDepositAlreadyTrue]);

  const handleClickCancel = () => {
    navigate(-1);
  };

  const buildDepositFlag = () => {
    if (isDepositAlreadyTrue) return undefined;
    return isPayed;
  };

  const handleSave = () => {
    if (!selectedRow) return;

    const stage = getStageKey(selectedRow.stage);

    const geometry = convertedGeometry[stage];
    if (!geometry) {
      return;
    }

    const result = validateConvertedGeometry(geometry);

    setValidationResult(result);
    setDepositPayedForSubmit(buildDepositFlag());
    setIsVisibleModalVerifiedLoadedObjects(true);
  };

  const openApproveReject = (action: "approve" | "reject") => {
    if (!selectedRow) return;

    const stage = getStageKey(selectedRow.stage);

    const geometry = convertedGeometry[stage];
    if (!geometry) {
      return;
    }

    const result = validateConvertedGeometry(geometry);
    setValidationResult(result);

    if (!result.isValid) {
      setIsVisibleModalVerifiedLoadedObjects(true);
      return;
    }

    setDepositPayedForSubmit(buildDepositFlag());
    setApprovalAction(action);
    setIsVisibleApproveRejectModal(true);
  };

  const handleCheckboxPayedChange = () => {
    setIsPayed((prevState) => !prevState);
  };

  const handleCloseApproveRejectModal = () => {
    setIsVisibleApproveRejectModal(false);
  };

  if (!selectedRow) {
    return null;
  }

  return (
    <>
      <div className={styles.masterUtilities}>
        <div className={styles.masterUtilitiesWrapper}>
          <div className={styles.header}>
            <h2 className={styles.title}>{t("master-utilities.project")}</h2>
            <div className={styles.metaInfo}>
              <StageTag stage={selectedRow.stage} />
              <StatusCell
                classes={{ status: styles.tagDesc }}
                status={selectedRow.status}
              />
            </div>
            <button onClick={handleClickCancel} className={styles.closeBtn}>
              <img src={close} alt="close" />
            </button>
          </div>
          <CustomScrollbar>
            <div className={styles.scrollContent}>
              <div className={styles.infoGrid}>
                <span className={styles.label}>
                  {t("master-utilities.workPermitStatus")}
                </span>
                <div>
                  <p className={styles.value}>{selectedRow.workPermitStage}</p>
                  {selectedRow.buildingPermitNumber &&
                    selectedRow.buildingPermitStage &&
                    selectedRow.buildingPermitIssueDate &&
                    selectedRow.buildingPermitExpireDate && (
                      <p className={`${styles.value} ${styles.permitDetails}`}>
                        Building permit is {selectedRow.buildingPermitStage} (
                        {selectedRow.buildingPermitIssueDate} —{" "}
                        {selectedRow.buildingPermitExpireDate})
                      </p>
                    )}
                </div>

                <span className={styles.label}>
                  {t("master-utilities.city")}
                </span>
                <p className={styles.value}>{selectedRow.workPermitEstate}</p>

                <span className={styles.label}>
                  {t("master-utilities.duration")}
                </span>
                <p className={styles.value}>
                  {selectedRow.issueDate} — {selectedRow.expireDate}
                </p>

                <span className={styles.label}>
                  {t("master-utilities.contractor")}
                </span>
                <p className={styles.value}>{selectedRow.contractor}</p>

                <span className={styles.label}>
                  {t("master-utilities.descriptionOfWork")}
                </span>
                <p className={styles.value}>{selectedRow.descriptionOfWork}</p>

                {!lock?.hideDepositPayedRow && (
                  <>
                    <span className={styles.label}>
                      {t("master-utilities.deposit")}
                    </span>
                    <Checkbox
                      classes={{
                        label: styles.value,
                      }}
                      checked={isPayed}
                      onChange={handleCheckboxPayedChange}
                      disabled={depositCheckboxDisabled}
                      label={t("master-utilities.payed")}
                    />
                  </>
                )}
              </div>
              <UploadFilesSection />
              <SubmissionHistory />
            </div>
          </CustomScrollbar>
          <div className={styles.bottomBtns}>
            {isContractor ? (
              <>
                <Button
                  onClick={() => handleSave()}
                  type="primary"
                  className={`${styles.btn} ${styles.saveBtn}`}
                  disabled={lock?.saveAndVerifyButton}
                >
                  {t("master-utilities.saveVerify")}
                </Button>
                <Button
                  onClick={handleClickCancel}
                  type="outline"
                  className={styles.btn}
                >
                  {t("master-utilities.cancel")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => openApproveReject("approve")}
                  type="primary"
                  className={styles.btn}
                  disabled={lock?.approveButton}
                >
                  {t("master-utilities.approve")}
                </Button>
                <Button
                  onClick={() => openApproveReject("reject")}
                  type="outline"
                  className={styles.btn}
                  disabled={lock?.rejectButton}
                >
                  {t("master-utilities.reject")}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {isVisibleModalVerifiedLoadedObjects && validationResult && (
        <VerifiedModal
          onClose={() => setIsVisibleModalVerifiedLoadedObjects(false)}
          validation={validationResult}
        />
      )}

      {isVisibleApproveRejectModal && approvalAction && (
        <ApproveRejectModal
          action={approvalAction}
          onClose={handleCloseApproveRejectModal}
          depositPayed={depositPayedForSubmit}
        />
      )}
    </>
  );
};

export default MasterUtilities;
